import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import type { OutstandingInvoice } from '../types/report.types';

// Component props
interface OutstandingBalanceReportTableProps {
  data: OutstandingInvoice[];
  columns?: ColumnDef<OutstandingInvoice, unknown>[];
  isLoading?: boolean;
  className?: string;
}

export const OutstandingBalanceReportTable: React.FC<
  OutstandingBalanceReportTableProps
> = ({
  data,
  columns = outstandingBalanceColumns,
  isLoading = false,
  className = '',
}) => {
  const table = useReactTable<OutstandingInvoice>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return (
      <div className="p-4 space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-300 animate-pulse rounded" />
        ))}
      </div>
    );

  return (
    <div
      className={`min-w-3xl ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="p-2 md:p-4 text-sm md:text-base text-center border">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-2 md:p-4 text-sm md:text-base text-start border">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-start text-gray-400"
              >
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Columns definition for Outstanding Balance
const outstandingBalanceColumns: ColumnDef<OutstandingInvoice>[] = [
  {
    accessorKey: 'id',
    header: () => <div className="text-center font-bold">Invoice ID</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'resident',
    header: () => <div className="font-bold">Resident</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'dueDate',
    header: () => <div className="text-center font-bold">Due Date</div>,
    cell: (info) => {
      const dueDate = info.getValue() as string;
      return (
        <div className="text-center">
          {new Date(dueDate).toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          })}
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: () => <div className="text-center font-bold">Status</div>,
    cell: (info) => {
      const status = String(info.getValue() ?? '-');
      const statusColor =
        status === 'OVERDUE'
          ? 'text-red-600'
          : status === 'PENDING'
          ? 'text-yellow-600'
          : 'text-gray-600';
      return <div className={`text-center ${statusColor}`}>{status}</div>;
    },
  },
  {
    accessorKey: 'amount',
    header: () => <div className="text-center font-bold">Amount</div>,
    cell: (info) => (
      <div className="text-center text-red-600 font-semibold">
        ${String(info.getValue() ?? 0)}
      </div>
    ),
  },
];
